import { sfQuery } from "../../API";

const directorUserRole = "Client Experience Director";
const coordinatorUserRole = "Client Experience Coordinator";

// Helper function to calculate business days between two dates
const getBusinessDaysBetween = (startDate, endDate) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  let count = 0;
  const curDate = new Date(start.getTime());
  while (curDate <= end) {
    const dayOfWeek = curDate.getDay();
    if (dayOfWeek !== 0 && dayOfWeek !== 6) count++;
    curDate.setDate(curDate.getDate() + 1);
  }
  return count;
};

// Get list of directors for the filter dropdown
export const getDirectorsList = async (context) => {
  const directors = await sfQuery(
    "User",
    "Id, Name",
    `IsActive = true AND UserRole.Name = '${directorUserRole}'`,
    context
  );
  return directors.map((user) => ({ id: user.Id, name: user.Name }));
};

// Helper function to group data by owner
const groupByOwner = (data, ownerField = "Owner.Name") => {
  const breakdown = {};
  data.forEach((item) => {
    const ownerName = getNestedValue(item, ownerField) || "Unknown";
    breakdown[ownerName] = (breakdown[ownerName] || 0) + 1;
  });
  return breakdown;
};

// Helper function to get nested object values
const getNestedValue = (obj, path) => {
  return path.split(".").reduce((current, key) => current?.[key], obj);
};

// Get active memberships count
export const getActiveMemberships = async (
  context,
  selectedDirector = null
) => {
  let whereClause = `Membership_Status__c = 'Activated' AND (NOT Account.Name LIKE 'Test %')`;

  if (selectedDirector) {
    whereClause += ` AND OwnerId = '${selectedDirector}'`;
  }

  const activeMemberships = await sfQuery(
    "Account",
    "Id, Name, OwnerId, Owner.Name, RecordType.Name",
    whereClause,
    context
  );

  // Count only Customer and Customer (CA) for the main number
  const customerAccounts = activeMemberships.filter((account) => {
    const recordType = account.RecordType?.Name || "";
    return recordType === "Customer" || recordType === "Customer (CA)";
  });

  const result = {
    count: customerAccounts.length,
    data: activeMemberships,
  };

  // Add breakdown by RecordType to show all types
  const recordTypeBreakdown = {};
  activeMemberships.forEach((account) => {
    const recordType = account.RecordType?.Name || "Unknown";
    recordTypeBreakdown[recordType] =
      (recordTypeBreakdown[recordType] || 0) + 1;
  });
  result.breakdown = recordTypeBreakdown;

  return result;
};

// Get overdue tasks count
export const getOverdueTasks = async (context, selectedDirector = null) => {
  let whereClause = `Status = 'Open' AND Owner.UserRole.Name = '${directorUserRole}' AND ActivityDate < TODAY AND WhatId IN (SELECT Id FROM Account WHERE Membership_Status__c = 'Activated')`;

  if (selectedDirector) {
    whereClause += ` AND OwnerId = '${selectedDirector}'`;
  }

  const result = await sfQuery(
    "Task",
    "Id, Subject, WhatId, What.Name, ActivityDate, Status, Owner.Name, OwnerId",
    whereClause,
    context
  );

  const response = { count: result.length, data: result };

  // Add breakdown by owner if no specific director is selected
  if (!selectedDirector) {
    response.breakdown = groupByOwner(result, "Owner.Name");
  }

  return response;
};

// Get accounts with no open activities
export const getAccountsWithNoOpenActivities = async (
  context,
  selectedDirector = null
) => {
  // First get all accounts
  let accountWhereClause = "Type = 'Client'";
  if (selectedDirector) {
    accountWhereClause += ` AND OwnerId = '${selectedDirector}'`;
  }

  const allAccounts = await sfQuery(
    "Account",
    "Id, Name, Owner.Name, OwnerId, (SELECT Id FROM Tasks WHERE Status = 'Open')",
    accountWhereClause,
    context
  );

  const accountsWithNoTasks = allAccounts.filter(
    (account) => !account.Tasks || account.Tasks.records.length === 0
  );

  const response = {
    count: accountsWithNoTasks.length,
    data: accountsWithNoTasks,
  };

  // Add breakdown by owner if no specific director is selected
  if (!selectedDirector) {
    response.breakdown = groupByOwner(accountsWithNoTasks, "Owner.Name");
  }

  return response;
};

// Get DAR tasks this month
export const getDARTasksThisMonth = async (
  context,
  selectedDirector = null
) => {
  let whereClause = `Task_Type__c = 'DAR' AND Owner.UserRole.Name = '${directorUserRole}' AND Status = 'Completed' AND ActivityDate = THIS_MONTH`;

  if (selectedDirector) {
    whereClause += ` AND OwnerId = '${selectedDirector}'`;
  }

  const result = await sfQuery(
    "Task",
    "Id, ActivityDate, CompletedDateTime, What.Name, Status, Owner.Name, OwnerId",
    whereClause,
    context
  );

  // Calculate color based on count (need 60+ per month to be green)
  const count = result.length;
  let response;

  if (selectedDirector) {
    // For individual director, use simple count logic
    response = {
      count: count,
      Green: count >= 60 ? count : 0,
      Yellow: count >= 30 && count < 60 ? count : 0,
      Red: count < 30 ? count : 0,
    };
  } else {
    // For all directors, group by owner and calculate colors
    const ownerBreakdown = groupByOwner(result, "Owner.Name");
    let totalGreen = 0,
      totalYellow = 0,
      totalRed = 0;

    Object.values(ownerBreakdown).forEach((count) => {
      if (count >= 60) totalGreen += count;
      else if (count >= 30) totalYellow += count;
      else totalRed += count;
    });

    response = {
      count: count,
      Green: totalGreen,
      Yellow: totalYellow,
      Red: totalRed,
      breakdown: ownerBreakdown,
    };
  }

  return response;
};

// Get DAR tasks this week
export const getDARTasksThisWeek = async (context, selectedDirector = null) => {
  let whereClause = `Task_Type__c = 'DAR' AND Owner.UserRole.Name = '${directorUserRole}' AND Status = 'Completed' AND ActivityDate = THIS_WEEK`;

  if (selectedDirector) {
    whereClause += ` AND OwnerId = '${selectedDirector}'`;
  }

  const result = await sfQuery(
    "Task",
    "Id, WhatId, What.Name, ActivityDate, Status, Owner.Name, OwnerId",
    whereClause,
    context
  );

  // Calculate color based on count (need 15+ per week to be green)
  const count = result.length;
  let response;

  if (selectedDirector) {
    response = {
      count: count,
      color: count >= 15 ? "green" : count >= 8 ? "yellow" : "red",
      data: result,
    };
  } else {
    const ownerBreakdown = groupByOwner(result, "Owner.Name");
    response = {
      count: count,
      data: result,
      breakdown: ownerBreakdown,
    };
  }

  return response;
};

// Get upsells this month
export const getUpsellsThisMonth = async (context, selectedDirector = null) => {
  let whereClause = `StageName = 'Closed Won' AND Owner.UserRole.Name = '${directorUserRole}' AND RecordType.Name LIKE '%Upsell%' AND CloseDate = THIS_MONTH`;

  if (selectedDirector) {
    whereClause += ` AND OwnerId = '${selectedDirector}'`;
  }

  const result = await sfQuery(
    "Opportunity",
    "Id, Name, CloseDate, Total_Amount__c, Owner.Name, OwnerId, AccountId, Account.Name",
    whereClause,
    context
  );

  const count = result.length;
  const totalAmount = result.reduce(
    (sum, opp) => sum + (opp.Total_Amount__c || 0),
    0
  );

  let response = {
    count: count,
    totalAmount: totalAmount,
    data: result,
    color: count > 1 ? "green" : count === 1 ? "yellow" : "red",
  };

  // Add breakdown by owner if no specific director is selected
  if (!selectedDirector) {
    const ownerBreakdown = {};
    const ownerAmountBreakdown = {};

    result.forEach((opp) => {
      const ownerName = opp.Owner?.Name || "Unknown";
      ownerBreakdown[ownerName] = (ownerBreakdown[ownerName] || 0) + 1;
      ownerAmountBreakdown[ownerName] =
        (ownerAmountBreakdown[ownerName] || 0) + (opp.Total_Amount__c || 0);
    });

    response.breakdown = ownerBreakdown;
    response.amountBreakdown = ownerAmountBreakdown;
  }

  return response;
};

// Get tasks completed this week
export const getTasksCompletedThisWeek = async (
  context,
  selectedDirector = null
) => {
  let whereClause = `Status = 'Completed' AND Owner.UserRole.Name = '${directorUserRole}' AND ActivityDate = THIS_WEEK`;

  if (selectedDirector) {
    whereClause += ` AND OwnerId = '${selectedDirector}'`;
  }

  const result = await sfQuery(
    "Task",
    "Id, Subject, WhatId, What.Name, ActivityDate, Status, Owner.Name, CompletedDateTime, OwnerId",
    whereClause,
    context
  );

  const response = { count: result.length, data: result };

  // Add breakdown by owner if no specific director is selected
  if (!selectedDirector) {
    response.breakdown = groupByOwner(result, "Owner.Name");
  }

  return response;
};

// Get tasks completed this month
export const getTasksCompletedThisMonth = async (
  context,
  selectedDirector = null
) => {
  let whereClause = `Status = 'Completed' AND Owner.UserRole.Name = '${directorUserRole}' AND ActivityDate = THIS_MONTH`;

  if (selectedDirector) {
    whereClause += ` AND OwnerId = '${selectedDirector}'`;
  }

  const tasksCompletedThisMonth = await sfQuery(
    "Task",
    "Id, Subject, WhatId, What.Name, ActivityDate, Status, Owner.Name, OwnerId",
    whereClause,
    context
  );

  const response = {
    count: tasksCompletedThisMonth.length,
    data: tasksCompletedThisMonth,
  };

  // Add breakdown by owner if no specific director is selected
  if (!selectedDirector) {
    response.breakdown = groupByOwner(tasksCompletedThisMonth, "Owner.Name");
  }

  return response;
};

// Get accounts by product
export const getAccountsByProduct = async (
  context,
  selectedDirector = null
) => {
  let whereClause =
    "Order.Status = 'Activated' AND Is_Active__c = TRUE AND Paused__c = false AND Is_Cancelled__c = false AND Order.Order_Label__c LIKE 'GM-%' AND PricebookEntry.Product2.ProductCode NOT IN ('GM', 'DM', 'GMACTR', 'GMPCD')";

  if (selectedDirector) {
    whereClause += ` AND Order.Account.OwnerId = '${selectedDirector}'`;
  }

  const result = await sfQuery(
    "OrderItem",
    "PricebookEntry.Product2.Name, Order.Account.Name, Order.AccountId, Order.Account.Owner.Name, Order.Account.OwnerId",
    whereClause,
    context
  );

  const productCounts = result.reduce((acc, curr) => {
    const productName = curr.PricebookEntry?.Product2?.Name || "Unknown";

    // Group similar products
    let groupedProduct = productName;
    if (productName.startsWith("Website")) {
      groupedProduct = "Website/SEO";
    } else if (productName.startsWith("Social Media")) {
      groupedProduct = "Social Media";
    } else if (productName.startsWith("Social+")) {
      groupedProduct = "Social+";
    } else if (productName.startsWith("Gargle Social+")) {
      groupedProduct = "Social+";
    } else if (productName.startsWith("Google Ads")) {
      groupedProduct = "Google Ads";
    } else if (productName.startsWith("Swell")) {
      groupedProduct = "Swell";
    } else if (productName.startsWith("Call Tracking")) {
      groupedProduct = "Call Tracking";
    } else if (productName.startsWith("Gargle Data")) {
      groupedProduct = "Gargle Data";
    } else if (productName.startsWith("Full Suite")) {
      groupedProduct = "Full Suite";
    } else if (productName.startsWith("Ad Agency")) {
      groupedProduct = "Ad Agency";
    }

    acc[groupedProduct] = (acc[groupedProduct] || 0) + 1;
    return acc;
  }, {});

  // Add breakdown by owner if no specific director is selected
  if (!selectedDirector) {
    const ownerBreakdown = {};
    result.forEach((item) => {
      const ownerName = item.Order?.Account?.Owner?.Name || "Unknown";
      ownerBreakdown[ownerName] = (ownerBreakdown[ownerName] || 0) + 1;
    });
    productCounts.breakdown = ownerBreakdown;
  }

  return productCounts;
};

// Get overdue invoices
export const getOverdueInvoices = async (context, selectedDirector = null) => {
  let whereClause = `Account.Membership_Status__c = 'Activated' AND Order_Label__c LIKE 'MI-%' AND Status = 'Activated' AND Payment_Date_new__c < TODAY AND Amount_Owed__c > 0`;

  if (selectedDirector) {
    whereClause += ` AND Account.OwnerId = '${selectedDirector}'`;
  }

  const result = await sfQuery(
    "Order",
    "Id, Account.Name, Payment_Date_new__c, Total_Amount__c, Account.Owner.Name, Account.OwnerId",
    whereClause,
    context
  );

  const today = new Date();
  const result2 = {
    Green: 0,
    Yellow: 0,
    Red: 0,
  };

  result.forEach((order) => {
    const dueDate = new Date(order.Payment_Date_new__c);
    const overdueDays = Math.floor((today - dueDate) / (1000 * 60 * 60 * 24));
    if (overdueDays <= 5) result2.Yellow++;
    else result2.Red++;
  });

  // Add breakdown by owner if no specific director is selected
  if (!selectedDirector) {
    result2.breakdown = groupByOwner(result, "Account.Owner.Name");
  }

  return result2;
};

// Get director account reviews
export const getDirectorAccountReviews = async (
  context,
  selectedDirector = null
) => {
  let whereClause = "Type = 'Director Account Review' AND Status = 'Completed'";

  if (selectedDirector) {
    whereClause += ` AND OwnerId = '${selectedDirector}'`;
  }

  const result = await sfQuery(
    "Task",
    "Id, ActivityDate, CompletedDateTime, What.Name, Status, Owner.Name, OwnerId",
    whereClause,
    context
  );

  const chartResult = {
    Red: 0,
    Yellow: 0,
    Green: 0,
  };

  result.forEach((task) => {
    if (task.ActivityDate && task.CompletedDateTime) {
      const daysBetween = getBusinessDaysBetween(
        new Date(task.ActivityDate),
        new Date(task.CompletedDateTime)
      );

      if (daysBetween <= 1) {
        chartResult.Green++;
      } else if (daysBetween <= 3) {
        chartResult.Yellow++;
      } else {
        chartResult.Red++;
      }
    }
  });

  // Add breakdown by owner if no specific director is selected
  if (!selectedDirector) {
    chartResult.breakdown = groupByOwner(result, "Owner.Name");
  }

  return chartResult;
};

// Get upcoming GM order expirations
export const getUpcomingGMOrderExpirations = async (
  context,
  selectedDirector = null
) => {
  const today = new Date();
  const futureDate = new Date();
  futureDate.setDate(today.getDate() + 45); // 45 days from now

  const todayString = today.toISOString().split("T")[0];
  const futureDateString = futureDate.toISOString().split("T")[0];

  let whereClause = `Account.Membership_Status__c = 'Activated' AND Order_Label__c LIKE 'GM-%' AND Contract_Expiration_Date__c >= ${todayString} AND Contract_Expiration_Date__c <= ${futureDateString}`;

  if (selectedDirector) {
    whereClause += ` AND Account.OwnerId = '${selectedDirector}'`;
  }

  const result = await sfQuery(
    "Order",
    "Id, Contract_Expiration_Date__c, Account.Name, Account.Owner.Name, Account.OwnerId, Order_Label__c",
    whereClause,
    context
  );

  const result2 = {
    Green: 0,
    Yellow: 0,
    Red: 0,
  };

  result.forEach((order) => {
    const expirationDate = new Date(order.Contract_Expiration_Date__c);
    const diffTime = expirationDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 10) {
      result2.Red++;
    } else if (diffDays < 20) {
      result2.Yellow++;
    } else {
      result2.Green++;
    }
  });

  // Add breakdown by owner if no specific director is selected
  if (!selectedDirector) {
    result2.breakdown = groupByOwner(result, "Account.Owner.Name");
  }

  return result2;
};

// Get paused products
export const getPausedProducts = async (context, selectedDirector = null) => {
  let whereClause = `Paused__c = true AND Order.Account.Membership_Status__c = 'Activated' AND Order.Order_Label__c LIKE 'GM-%'`;

  if (selectedDirector) {
    whereClause += ` AND Order.Account.OwnerId = '${selectedDirector}'`;
  }

  const result = await sfQuery(
    "OrderItem",
    "Id, Paused__c, Paused_Date__c, Order.Account.Name, Order.Account.Owner.Name, Order.Account.OwnerId",
    whereClause,
    context
  );

  const today = new Date();
  const result2 = {
    Green: 0,
    Yellow: 0,
    Red: 0,
  };

  result.forEach((item) => {
    if (!item.Paused_Date__c) {
      result2.Red++;
      return;
    }

    const pausedUntil = new Date(item.Paused_Date__c);
    const diffTime = Math.abs(pausedUntil - today);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays <= 7) {
      result2.Green++;
    } else if (diffDays <= 14) {
      result2.Yellow++;
    } else {
      result2.Red++;
    }
  });

  // Add breakdown by owner if no specific director is selected
  if (!selectedDirector) {
    result2.breakdown = groupByOwner(result, "Order.Account.Owner.Name");
  }

  return result2;
};

// Get completed director account reviews
export const getCompletedDirectorAccountReviews = async (
  context,
  selectedDirector = null
) => {
  let whereClause = `Subject LIKE '%Director Account Review%' AND Owner.UserRole.Name = '${directorUserRole}' AND Status = 'Completed'`;

  if (selectedDirector) {
    whereClause += ` AND OwnerId = '${selectedDirector}'`;
  }

  const result = await sfQuery(
    "Task",
    "Id, ActivityDate, CompletedDateTime, What.Name, Status, Owner.Name, OwnerId",
    whereClause,
    context
  );

  const today = new Date();
  const result2 = {
    Green: 0,
    Yellow: 0,
    Red: 0,
  };

  result.forEach((task) => {
    const completedDate = new Date(task.CompletedDateTime);
    const activityDate = new Date(task.ActivityDate);
    const diffTime = Math.abs(activityDate - completedDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays <= 1) {
      result2.Green++;
    } else if (diffDays <= 2) {
      result2.Yellow++;
    } else {
      result2.Red++;
    }
  });

  return result2;
};

// Get completion of SAI tasks
export const getCompletionOfSAItasks = async (
  context,
  selectedDirector = null
) => {
  let whereClause = `Subject LIKE '%SAI%' AND Owner.UserRole.Name = '${directorUserRole}' AND Status = 'Completed' AND ActivityDate = THIS_MONTH`;

  if (selectedDirector) {
    whereClause += ` AND OwnerId = '${selectedDirector}'`;
  }

  const result = await sfQuery(
    "Task",
    "Id, Subject, ActivityDate, CompletedDateTime, Status, Owner.Name, OwnerId",
    whereClause,
    context
  );

  const result2 = {
    Green: 0,
    Yellow: 0,
    Red: 0,
  };

  result.forEach((task) => {
    const completedDate = new Date(task.CompletedDateTime);
    const activityDate = new Date(task.ActivityDate);
    const diffTime = Math.abs(activityDate - completedDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays <= 1) {
      result2.Green++;
    } else if (diffDays <= 2) {
      result2.Yellow++;
    } else {
      result2.Red++;
    }
  });

  // Add breakdown by owner if no specific director is selected
  if (!selectedDirector) {
    result2.breakdown = groupByOwner(result, "Owner.Name");
  }

  return result2;
};

// Get open tasks for bottom table
export const getOpenTasks = async (context, selectedDirector = null) => {
  let whereClause = `Status = 'Open' AND Owner.UserRole.Name = '${directorUserRole}' AND ActivityDate <= NEXT_WEEK AND (NOT Subject LIKE 'Unresolved Email:%')`;

  if (selectedDirector) {
    whereClause += ` AND OwnerId = '${selectedDirector}'`;
  }

  const result = await sfQuery(
    "Task",
    "Id, Subject, Task_Type__c, ActivityDate, Description, Owner.Name, OwnerId, What.Name",
    whereClause,
    context
  );

  return result.map((task) => ({
    ...task,
    AccountName: task.What?.Name || "Unknown",
    TaskType: task.Task_Type__c || "Unknown",
    Date: task.ActivityDate,
    Details: task.Description || "No details",
    Owner: task.Owner?.Name || "Unknown",
  }));
};

// ====================================
// DATATABLE-SPECIFIC QUERY FUNCTIONS
// ====================================
// These functions return actual record arrays for DataTable display

export const getUpcomingGMOrderExpirationsForTable = async (context) => {
  const today = new Date();
  const futureDate = new Date();
  futureDate.setDate(today.getDate() + 45); // 45 days from now

  const todayString = today.toISOString().split("T")[0];
  const futureDateString = futureDate.toISOString().split("T")[0];

  const result = await sfQuery(
    "Order",
    "Id, Contract_Expiration_Date__c, Account.Name, Account.Owner.Name, Account.Id, Order_Label__c",
    `Account.Membership_Status__c = 'Activated' AND Order_Label__c LIKE 'GM-%' AND Contract_Expiration_Date__c >= ${todayString} AND Contract_Expiration_Date__c <= ${futureDateString}`,
    context
  );

  return result.map((order) => {
    const expirationDate = new Date(order.Contract_Expiration_Date__c);
    const diffTime = expirationDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return {
      ...order,
      Id: order.Account?.Id,
      Name: order.Account?.Name || "Unknown Account",
      Owner: { Name: order.Account?.Owner?.Name || "Unknown" },
      ExpirationDate: order.Contract_Expiration_Date__c,
      DaysUntilExpiration: diffDays,
      OrderLabel: order.Order_Label__c,
      AccountId: order.Account?.Id,
    };
  });
};

export const getDirectorAccountReviewsForTable = async (context) => {
  const result = await sfQuery(
    "Task",
    "Id, ActivityDate, CompletedDateTime, What.Name, Status, Subject, Owner.Name, WhatId",
    `Subject LIKE '%Director Account Review%' AND Owner.UserRole.Name = '${directorUserRole}' AND Status = 'Completed' AND ActivityDate = THIS_MONTH`,
    context
  );

  return result.map((task) => {
    const completedDate = new Date(task.CompletedDateTime);
    const activityDate = new Date(task.ActivityDate);
    const diffTime = Math.abs(activityDate - completedDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return {
      ...task,
      Name: task.What?.Name || "Unknown Account",
      Owner: { Name: task.Owner?.Name || "Unknown" },
      ReviewDate: task.ActivityDate,
      DaysBetween: diffDays,
      AccountId: task.WhatId,
    };
  });
};

export const getOverdueInvoicesForTable = async (context) => {
  const result = await sfQuery(
    "Order",
    "Id, Account.Name, Account.Owner.Name, Payment_Date_new__c, Total_Amount__c, AccountId",
    `Account.Membership_Status__c = 'Activated' AND Order_Label__c LIKE 'MI-%' AND Status = 'Activated' AND Payment_Date_new__c < TODAY AND Amount_Owed__c > 0`,
    context
  );

  return result.map((order) => ({
    ...order,
    Id: order.AccountId, // Use AccountId as the record ID for navigation
    Name: order.Account?.Name || "Unknown Account",
    Owner: { Name: order.Account?.Owner?.Name || "Unknown" },
    DueDate: order.Payment_Date_new__c,
    Amount: order.Total_Amount__c,
    InvoiceNumber: order.Id, // Use Order ID as invoice number
  }));
};

export const getPausedProductsForTable = async (context) => {
  const result = await sfQuery(
    "OrderItem",
    "Id, Paused__c, Paused_Date__c, Order.Account.Name, Order.Account.Owner.Name, Order.AccountId, PricebookEntry.Product2.Name",
    `Paused__c = true AND Order.Account.Membership_Status__c = 'Activated' AND Order.Order_Label__c LIKE 'GM-%'`,
    context
  );

  return result.map((item) => ({
    ...item,
    Id: item.Order?.AccountId, // Use AccountId for navigation
    Name: item.Order?.Account?.Name || "Unknown Account",
    Owner: { Name: item.Order?.Account?.Owner?.Name || "Unknown" },
    Product: item.PricebookEntry?.Product2?.Name || "Unknown Product",
    ExpirationDate: item.Paused_Date__c,
    AccountId: item.Order?.AccountId,
  }));
};

export const getCompletionOfSAItasksForTable = async (context) => {
  const result = await sfQuery(
    "Task",
    "Id, Subject, ActivityDate, CompletedDateTime, Status, Owner.Name, What.Name, WhatId",
    `Subject LIKE '%SAI%' AND Owner.UserRole.Name = '${directorUserRole}' AND Status = 'Completed' AND ActivityDate = THIS_MONTH`,
    context
  );

  return result.map((task) => {
    const completedDate = new Date(task.CompletedDateTime);
    const activityDate = new Date(task.ActivityDate);
    const diffTime = Math.abs(activityDate - completedDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return {
      ...task,
      Name: task.What?.Name || "Unknown Account",
      Owner: { Name: task.Owner?.Name || "Unknown" },
      CompletionDate: task.CompletedDateTime,
      DaysBetween: diffDays,
      AccountId: task.WhatId,
    };
  });
};

export const getAccountsByProductForTable = async (context) => {
  const accountsByProduct = await sfQuery(
    "OrderItem",
    "PricebookEntry.Product2.Name, Order.Account.Name, Order.Account.Owner.Name, Order.AccountId, Order.Status",
    `Order.Account.Membership_Status__c = 'Activated' AND Order.Order_Label__c LIKE 'GM-%' AND Order.Status IN ('Activated', 'Ala Carte Non Member') AND Is_Active__c = TRUE AND Product_Available__c = TRUE AND Is_Cancelled__c = FALSE AND (NOT Product2.ProductCode IN ('PC', 'DMPC', 'NM', 'DMNM', 'RC', 'DMRC'))`,
    context
  );

  return accountsByProduct.map((item) => ({
    Id: item.Order?.AccountId,
    Name: item.Order?.Account?.Name || "Unknown Account",
    Owner: { Name: item.Order?.Account?.Owner?.Name || "Unknown" },
    Product: item.PricebookEntry?.Product2?.Name || "Unknown Product",
    Status: item.Order?.Status || "Unknown",
    AccountId: item.Order?.AccountId,
  }));
};

export const getActiveMembershipsForTable = async (context) => {
  const activeMemberships = await sfQuery(
    "Account",
    "Id, Name, Owner.Name, Membership_Status__c, Activated_Date__c",
    `Membership_Status__c = 'Activated' AND (NOT Account.Name LIKE 'Test %') AND RecordType.Name = 'Customer'`,
    context
  );

  return activeMemberships.map((account) => ({
    ...account,
    Owner: { Name: account.Owner?.Name || "Unknown" },
    Status: account.Membership_Status__c,
    ActivatedDate: account.Activated_Date__c,
  }));
};

export const getDARTasksThisMonthForTable = async (context) => {
  const result = await sfQuery(
    "Task",
    "Id, ActivityDate, CompletedDateTime, What.Name, Status, Subject, Owner.Name, WhatId",
    `Task_Type__c = 'DAR' AND Owner.UserRole.Name = '${directorUserRole}' AND Status = 'Completed' AND ActivityDate = THIS_MONTH`,
    context
  );

  return result.map((task) => ({
    ...task,
    Name: task.What?.Name || "Unknown Account",
    Owner: { Name: task.Owner?.Name || "Unknown" },
    TaskDate: task.ActivityDate,
    AccountId: task.WhatId,
  }));
};

export const getUpsellsThisMonthForTable = async (context) => {
  const result = await sfQuery(
    "Opportunity",
    "Id, Name, CloseDate, Total_Amount__c, Owner.Name, AccountId, Account.Name, StageName",
    `StageName = 'Closed Won' AND Owner.UserRole.Name = '${directorUserRole}' AND RecordType.Name LIKE '%Upsell%' AND CloseDate = THIS_MONTH`,
    context
  );

  return result.map((opp) => ({
    ...opp,
    Name: opp.Account?.Name || "Unknown Account",
    Owner: { Name: opp.Owner?.Name || "Unknown" },
    OpportunityName: opp.Name,
    Amount: opp.Total_Amount__c || 0,
    CloseDate: opp.CloseDate,
    AccountId: opp.AccountId,
  }));
};

// Add new query functions for Directors dashboard cards

// New clients this month - Order with Order_Label__c like GM-% and status Activated and Activated_Date__c = this_month
export const getNewClientsThisMonth = async (
  context,
  selectedDirector = null
) => {
  let whereClause = `Order_Label__c LIKE 'GM-%' AND Status = 'Activated' AND Account.Activated_Date__c = THIS_MONTH`;

  if (selectedDirector) {
    whereClause += ` AND OwnerId = '${selectedDirector}'`;
  }

  const result = await sfQuery(
    "Order",
    "Id, Name, OwnerId, Owner.Name, Order_Label__c, Status, ActivatedDate",
    whereClause,
    context
  );

  const response = {
    count: result.length,
    data: result,
  };

  // Add breakdown by owner if no specific director is selected
  if (!selectedDirector) {
    response.breakdown = groupByOwner(result, "Owner.Name");
  }

  return response;
};

// No Up-sell Discussion - Accounts with Membership_Status__c = activated, activated_date__c > 90 days, Director_Up_Sell_Discussion_Date__c = null
export const getNoUpSellDiscussion = async (
  context,
  selectedDirector = null
) => {
  let whereClause = `Membership_Status__c = 'Activated' AND Activated_Date__c < LAST_N_DAYS:90 AND Director_Up_Sell_Discussion_Date__c = null`;

  if (selectedDirector) {
    whereClause += ` AND OwnerId = '${selectedDirector}'`;
  }

  const result = await sfQuery(
    "Account",
    "Id, Name, OwnerId, Owner.Name, Membership_Status__c, Activated_Date__c, Director_Up_Sell_Discussion_Date__c",
    whereClause,
    context
  );

  const response = {
    count: result.length,
    data: result,
  };

  // Add breakdown by owner if no specific director is selected
  if (!selectedDirector) {
    response.breakdown = groupByOwner(result, "Owner.Name");
  }

  return response;
};

// No Payment Confirmed - accounts with membership_status__c = activated and Valid_Payment_Method_in_Charge_Over__c = false and recordtype.name like 'Customer%'
export const getNoPaymentConfirmed = async (
  context,
  selectedDirector = null
) => {
  let whereClause = `Membership_Status__c = 'Activated' AND Valid_Payment_Method_in_Charge_Over__c = false AND RecordType.Name LIKE 'Customer%'`;

  if (selectedDirector) {
    whereClause += ` AND OwnerId = '${selectedDirector}'`;
  }

  const result = await sfQuery(
    "Account",
    "Id, Name, OwnerId, Owner.Name, Membership_Status__c, Valid_Payment_Method_in_Charge_Over__c, RecordType.Name",
    whereClause,
    context
  );

  const response = {
    count: result.length,
    data: result,
  };

  // Add breakdown by owner if no specific director is selected
  if (!selectedDirector) {
    response.breakdown = groupByOwner(result, "Owner.Name");
  }

  return response;
};

// High Risk - accounts with Membership_Status__c = activated, Risk_of_Cancellation__c = High Risk
export const getHighRiskAccounts = async (context, selectedDirector = null) => {
  let whereClause = `Membership_Status__c = 'Activated' AND Risk_of_Cancellation__c = 'High Risk'`;

  if (selectedDirector) {
    whereClause += ` AND OwnerId = '${selectedDirector}'`;
  }

  const result = await sfQuery(
    "Account",
    "Id, Name, OwnerId, Owner.Name, Membership_Status__c, Risk_of_Cancellation__c",
    whereClause,
    context
  );

  const response = {
    count: result.length,
    data: result,
    color: result.length > 1 ? "red" : "green",
  };

  // Add breakdown by owner if no specific director is selected
  if (!selectedDirector) {
    response.breakdown = groupByOwner(result, "Owner.Name");
  }

  return response;
};
